import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import styles from '../TablesList.module.scss'
import ChipsCell from '@/components/Table/components/ChipsCell/ChipsCell'
import IconButton from '@/components/IconButton/IconButton'
import { transparentColors } from '@/assets/styles/helpers/colors.styles'

const STATUS_CHIP_COLORS: Record<string, string> = {
  free: transparentColors.whiteShade5,
  occupied: transparentColors.orangeShade5,
  serving: transparentColors.orangeShade4,
  served: transparentColors.whiteShade2,
  paid: transparentColors.whiteShade3,
  needsCleaning: transparentColors.orangeShade6
}

type ColumnActions = {
  onEdit: (tableId: string) => void
  onMarkAsServed: (tableId: string, currentStatus: string) => void
}

const TICK_TRANSITIONS: Record<string, string> = {
  serving: 'served',
  paid: 'free'
}

export const useTablesListColumnConfig = (actions?: ColumnActions) => {
  const { t } = useTranslation()

  const columns = useMemo<GridColDef[]>(() => {
    const equalColumn: Partial<GridColDef> = {
      flex: 1,
      sortable: false,
      hideable: false,
      resizable: false,
      disableColumnMenu: true,
      headerClassName: styles.tableHeader,
      cellClassName: styles.tableCell
    }

    return [
      {
        field: '__check__',
        width: 52,
        minWidth: 52,
        maxWidth: 52,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        align: 'center',
        headerAlign: 'center',
        hideable: true
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 52,
        minWidth: 52,
        maxWidth: 52,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        align: 'center',
        headerAlign: 'center',
        hideable: true,
        renderHeader: params => (
          <p className={`${styles.tableHeaderText} title-bold-medium`}>
            {params.colDef.headerName}
          </p>
        )
      },
      {
        field: 'number',
        headerName: t('TABLE.TABLES_LIST.NUMBER'),
        ...equalColumn,
        renderHeader: params => (
          <p className={`${styles.tableHeaderText} title-bold-medium`}>
            {params.colDef.headerName}
          </p>
        )
      },
      {
        field: 'status',
        headerName: t('TABLE.TABLES_LIST.STATUS'),
        ...equalColumn,
        renderHeader: params => (
          <p className={`${styles.tableHeaderText} title-bold-medium`}>
            {params.colDef.headerName}
          </p>
        ),
        renderCell: params => {
          if (!Array.isArray(params.value)) {
            return <span>{String(params.value ?? '')}</span>
          }
          const translatedChips = params.value.map(
            (chip: { id: string; label: string; tooltipEvent: never[] }) => ({
              ...chip,
              label: t(`TABLE.STATUS.${chip.label.toUpperCase()}`) || chip.label
            })
          )
          const translatedColors: Record<string, string> = {}
          params.value.forEach((chip: { label: string }) => {
            const translated =
              t(`TABLE.STATUS.${chip.label.toUpperCase()}`) || chip.label
            translatedColors[translated] = STATUS_CHIP_COLORS[chip.label] ?? ''
          })
          return (
            <ChipsCell
              chipPayload={translatedChips}
              chipBackgroundColors={translatedColors}
            />
          )
        }
      },
      {
        field: 'guests',
        headerName: t('TABLE.TABLES_LIST.GUESTS'),
        ...equalColumn,
        renderHeader: params => (
          <p className={`${styles.tableHeaderText} title-bold-medium`}>
            {params.colDef.headerName}
          </p>
        )
      },
      {
        field: 'currentTotal',
        headerName: t('TABLE.TABLES_LIST.CURRENT_TOTAL'),
        ...equalColumn,
        renderHeader: params => (
          <p className={`${styles.tableHeaderText} title-bold-medium`}>
            {params.colDef.headerName}
          </p>
        )
      },
      {
        field: 'actions',
        headerName: t('TABLE.TABLES_LIST.ACTIONS') || 'Actions',
        ...equalColumn,
        flex: 0.8,
        sortable: false,
        filterable: false,
        renderHeader: params => (
          <p className={`${styles.tableHeaderText} title-bold-medium`}>
            {params.colDef.headerName}
          </p>
        ),
        renderCell: (params: GridRenderCellParams) => {
          const status: string = Array.isArray(params.row.status)
            ? (params.row.status[0]?.label ?? '')
            : ''

          if (status === 'free') return null

          const canTransition = status in TICK_TRANSITIONS

          return (
            <div className={styles.tableActionButtons}>
              <IconButton
                icon="las la-pen"
                onClick={e => {
                  e.stopPropagation()
                  actions?.onEdit(params.row.id)
                }}
                color="primary"
                variant="contained"
                disabled
              />
              <IconButton
                icon="las la-check"
                onClick={e => {
                  e.stopPropagation()
                  if (canTransition) {
                    actions?.onMarkAsServed(params.row.id, status)
                  }
                }}
                color="primary"
                variant="contained"
                disabled={!canTransition}
              />
            </div>
          )
        }
      }
    ]
  }, [t, actions])
  return columns as GridColDef[]
}
